import React, { useMemo } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    InputAdornment,
    Stack,
} from '@mui/material';
import { observer } from 'mobx-react-lite';
import CommonInput from '../../../../component/form/common-input.component';
import TableComponent, { HeadCell } from '../../../../component/table/table.component';
import { formatEUR } from '../../../../constants/tool.fn';
import i18n from '../../../../i18n';
import { ErrorMessageBox } from '../../data-analysis.page';
import ExtraAnalysisState from '../state/extra-analysis-state';
import { ExtraAnalysisRow } from '../state/dish-data-shared.types';
import { ReactComponent as SearchIcon } from '../../../../static/images/svg/ic_round-search.svg';
import { ReactComponent as SelectAllIcon } from '../../../../static/images/svg/fluent_select-all-on-16-regular.svg';
import { ReactComponent as CompareIcon } from '../../../../static/images/svg/material-symbols-light_chart-data-outline-rounded.svg';

/** 组件属性。 */
interface ExtraAnalysisTabPageProps {
    /** 额外费用分析状态。 */
    store: ExtraAnalysisState;
    /** 打开对比弹窗。 */
    onOpenCompareDialog: () => void;
}

/**
 * 顶部操作按钮统一样式（与菜品销售页签一致，对应 Figma 默认/悬停态）。
 * 默认：浅底、深色正文、图标为主题色；悬停：主题浅底 + 主题描边 + 主题色字与图标。
 */
const CONTROL_BUTTON_SX = {
    height: 56,
    minHeight: 56,
    borderRadius: '16px',
    px: '16px',
    py: 0,
    boxShadow: '0px 0px 4px var(--shadow-50)',
    whiteSpace: 'nowrap',
    textTransform: 'none',
    /** 与稿面正文一致：20px / 24px / Medium，走主题 typography.body1 */
    typography: 'body1',
    boxSizing: 'border-box',
    border: '1px solid transparent',
    bgcolor: 'var(--fill-neutral-100)',
    color: 'var(--text-dark)',
    // 图标区固定 40×40；默认/悬停同一尺寸
    '& .MuiButton-startIcon': {
        mr: '8px',
        width: 40,
        height: 40,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginLeft: 0,
    },
    '& .MuiButton-startIcon svg': {
        width: 40,
        height: 40,
        flexShrink: 0,
        display: 'block',
        color: 'var(--text-primary-300)',
    },
    '&:hover:not(.Mui-disabled)': {
        bgcolor: 'var(--fill-primary-200)',
        borderColor: 'var(--stroke-primary-300)',
        color: 'var(--text-primary-300)',
    },
    '&.Mui-disabled': {
        bgcolor: 'var(--fill-neutral-100)',
        color: 'var(--text-dark)',
        borderColor: 'transparent',
        opacity: 0.48,
    },
    '&.Mui-disabled .MuiButton-startIcon svg': {
        color: 'var(--text-primary-300)',
    },
    '&.Mui-disabled:hover': {
        bgcolor: 'var(--fill-neutral-100)',
        borderColor: 'transparent',
        color: 'var(--text-dark)',
    },
};

/**
 * 额外费用销售分析页签。
 */
const ExtraAnalysisTabPage = observer(({ store, onOpenCompareDialog }: ExtraAnalysisTabPageProps) => {
    /**
     * 读取指定列的前端排序箭头状态。
     * @param key - 表头列 id
     * @returns 0 未排序 / 1 降序 / 2 升序
     */
    const getSortStatus = (key: string) => {
        /** 当前排序配置。 */
        const sort = store.sortKeyValue;
        if (key === sort?.key) {
            return sort.value === 'ASC' ? 2 : 1;
        }
        return 0;
    };

    /**
     * 表头点击切换排序（无排序 → 降序 → 升序 → 无排序）。
     * @param headCell - 被点击的表头
     */
    const handleSort = (headCell: HeadCell) => {
        /** 下一档排序方向。 */
        let sortStatus: 'ASC' | 'DESC' | '' = '';
        switch (headCell.sortStatus) {
            case 0:
                sortStatus = 'DESC';
                break;
            case 1:
                sortStatus = 'ASC';
                break;
            case 2:
                sortStatus = '';
                break;
            default:
                sortStatus = '';
                break;
        }
        store.setSortValue({
            value: sortStatus ? sortStatus : undefined,
            key: sortStatus ? headCell.id : undefined,
        });
    };

    /** 搜索框输入样式。 */
    const searchInputProps = useMemo(() => ({
        endAdornment: (
            <InputAdornment position="end" sx={{ mr: 0 }}>
                <SearchIcon style={{ width: 24, height: 24, color: 'var(--text-primary-300)' }} />
            </InputAdornment>
        ),
    }), []);

    /** 表头配置，沿用旧页 `TableComponent` 结构。 */
    const headCellList = useMemo<HeadCell[]>(() => [
        {
            id: 'name',
            label: i18n.t('general_text_name'),
            sortStatus: getSortStatus('name'),
        },
        {
            id: 'sum_quantity',
            label: i18n.t('business_analysis_page_text_sales_quantity'),
            sortStatus: getSortStatus('sum_quantity'),
            renderFunction: (item) => String(item?.sum_quantity ?? 0),
        },
        {
            id: 'sum_price',
            label: i18n.t('business_analysis_page_text_sales_total'),
            sortStatus: getSortStatus('sum_price'),
            renderFunction: (item) => formatEUR(Number(item?.sum_price || 0)),
        },
        {
            id: 'count_percentage',
            label: i18n.t('business_analysis_page_text_sales_ratio'),
            sortStatus: getSortStatus('count_percentage'),
            renderFunction: (item) => item?.count_percentage != null ? `${item.count_percentage}%` : '--',
        },
    ], [store.sortKeyValue]);

    if (store.errorMessage) {
        return <ErrorMessageBox errorMessage={store.errorMessage} height={360} />;
    }

    return (
        <Stack spacing={3} sx={{ minHeight: 'calc(100vh - 260px)' }}>
            <Box
                sx={{
                    display: 'flex',
                    gap: 2,
                    justifyContent: 'space-between',
                    flexWrap: 'nowrap',
                    alignItems: 'center',
                    overflowX: 'auto',
                    overflowY: 'visible',
                    px: '4px',
                    py: '4px',
                }}
            >
                <Box sx={{ flex: 1, minWidth: 0 }} />
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'nowrap', alignItems: 'center', flexShrink: 0, ml: 2 }}>
                    <Box sx={{ width: 160, flexShrink: 0 }}>
                        <CommonInput
                            inputType="INPUT"
                            size="small"
                            placeholder={i18n.t('general_text_search')}
                            value={store.searchKeyword}
                            onChange={(event) => store.setSearchKeyword(event.target.value)}
                            InputProps={searchInputProps}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    height: 56,
                                    minHeight: 56,
                                    borderRadius: '16px',
                                    boxShadow: '0px 0px 4px var(--shadow-50)',
                                    bgcolor: 'var(--fill-neutral-100)',
                                    boxSizing: 'border-box',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'transparent',
                                    },
                                    '& input': {
                                        py: '15px',
                                        px: '16px',
                                        boxSizing: 'border-box',
                                        color: 'var(--text-primary-300)',
                                    },
                                    '& input::placeholder': {
                                        color: 'var(--text-primary-300)',
                                        opacity: 1,
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'var(--stroke-primary-300)',
                                    },
                                },
                            }}
                        />
                    </Box>
                    <Button
                        variant="outlined"
                        onClick={() => store.toggleSelectAllFilteredRows()}
                        disabled={store.filteredRows.length === 0}
                        startIcon={<SelectAllIcon />}
                        sx={{
                            ...CONTROL_BUTTON_SX,
                            minWidth: 108,
                            ...(store.isAllFilteredSelected && store.filteredRows.length > 0
                                ? {
                                      bgcolor: 'var(--fill-primary-200)',
                                      borderColor: 'var(--stroke-primary-300)',
                                      color: 'var(--text-primary-300)',
                                      '& .MuiButton-startIcon svg': {
                                          color: 'var(--text-primary-300)',
                                      },
                                  }
                                : {}),
                        }}
                    >
                        {i18n.t('business_analysis_page_button_select_all')}
                    </Button>
                    <Button
                        variant="outlined"
                        disabled={!store.canCompare}
                        onClick={onOpenCompareDialog}
                        startIcon={<CompareIcon />}
                        sx={{
                            ...CONTROL_BUTTON_SX,
                            minWidth: 132,
                        }}
                    >
                        {i18n.t('business_analysis_page_text_data_compare')}
                    </Button>
                </Box>
            </Box>
            <Box sx={{ minHeight: 0, flex: 1, display: 'flex' }}>
                <Card
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1,
                        minHeight: 0,
                        borderRadius: '16px',
                        boxShadow: '0px 0px 4px var(--shadow-25)',
                    }}
                >
                    <CardContent
                        sx={{
                            display: 'flex',
                            flex: 1,
                            minHeight: 0,
                            '&:last-child': { pb: 3 },
                        }}
                    >
                        <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
                            <Box sx={{ flex: 1, minHeight: 0 }}>
                            <TableComponent
                                isPagination
                                allCount={store.filteredRows.length}
                                headCellList={headCellList}
                                list={store.filteredRows}
                                showChecked={0}
                                compareSymbol="rowKey"
                                originSelect={store.selectedRows}
                                isTableLoading={store.loading}
                                selectedRowVariant="primary"
                                handleSort={handleSort}
                                onRowClick={(row) => store.toggleRowSelection(row as ExtraAnalysisRow)}
                            />
                            </Box>
                        </Stack>
                    </CardContent>
                </Card>
            </Box>
        </Stack>
    );
});

export default ExtraAnalysisTabPage;
